import { Close } from "@mui/icons-material";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { CustomButton } from "src/Components/Common/Button/CustomButton";
import { DIALOG_MAX_WIDTHS } from "src/Components/Common/Modal/ModalWidth";

import { FixedSizeList, ListChildComponentProps } from "react-window";
import { TagsInputUniqueKeys } from "src/Components/Recon360/ReconMIS/AutoCompletes";

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const option = data[index];

  const mergedStyle = {
    ...option.props.style, // ✅ preserves color/fontWeight from renderOption
    ...style, // ✅ includes positioning from react-window
    top: (style.top as number) + LISTBOX_PADDING,
  };

  return React.cloneElement(option, {
    ...option.props,
    style: mergedStyle,
  });
}

export const VirtualizedListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const items = React.Children.toArray(children);
    const ITEM_SIZE = 40;
    const height = Math.min(8, items.length) * ITEM_SIZE + 2 * LISTBOX_PADDING;
    const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => <div ref={ref} {...props} {...other} />);
    OuterElementType.displayName = "OuterElementType";
    return (
      <div ref={ref}>
        <FixedSizeList
          height={height}
          itemCount={items.length}
          itemSize={ITEM_SIZE}
          width="100%"
          itemData={items}
          outerElementType={OuterElementType}
          innerElementType="ul"
        >
          {renderRow}
        </FixedSizeList>
      </div>
    );
  }
);

export function useDebounce<T>(value: T, delay = 300): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer); // cleanup on new keystroke
  }, [value, delay]);

  return debouncedValue;
}

const EXCLUDE = "Exclude";

const NonReconVpFilterOfDisputedEntries = ({
  openFilterOfDisputedEntries,
  setOpenFilterOfDisputedEntries,
  filters,
  setFilters,
  data,
}) => {
  const getFilterValue = (filters: any[], id: string): string[] => {
    const filter = filters.find((f) => f.id === id);
    return Array.isArray(filter?.value) ? filter.value.filter((v) => v !== null && v !== undefined && v !== "") : [];
  };

  const [invoiceNumber, setVInvoiceNumber] = useState<string[]>(getFilterValue(filters, "invoiceNumber"));
  const [organizationName, setOrganizationName] = useState<string[]>(getFilterValue(filters, "organizationName"));
  const [poNumber, setPoNumber] = useState<string[]>(getFilterValue(filters, "poNumber"));

  // const [issueRaised, setIssueRaised] = useState<string[]>(getFilterValue(filters, "issueRaised"));
  const [instamartDNNumber, setInstamartDNNumber] = useState<string[]>(getFilterValue(filters, "instamartDNNumber"));

  //  performance optimization

  const [invoiceInput, setInvoiceInput] = useState("");
  const [instamartDNInput, setInstamartDNInput] = useState("");
  const [poInput, setPoInput] = useState("");
  const [organizationNameInput, setOrganizationNameInput] = useState("");

  const debouncedInvoiceInput = useDebounce(invoiceInput, 200);
  const debouncedInstamartDNInput = useDebounce(instamartDNInput, 200);
  const debouncedPoInput = useDebounce(poInput, 200);
  const debouncedOrganizationNameInput = useDebounce(organizationNameInput, 200);

  const handleClose = () => {
    setOpenFilterOfDisputedEntries(false);
  };

  const handleApplyFilter = () => {
    const newFilters = [];

    if (invoiceNumber.length > 0) {
      newFilters.push({ id: "invoiceNumber", value: invoiceNumber });
    }

    if (instamartDNNumber.length > 0) {
      newFilters.push({ id: "instamartDNNumber", value: instamartDNNumber });
    }

    if (organizationName.length > 0) {
      newFilters.push({ id: "organizationName", value: organizationName });
    }

    if (poNumber.length > 0) {
      newFilters.push({ id: "poNumber", value: poNumber });
    }

    // commentsFilter[0] === "All" => skip filter

    // Apply filters to MRT
    setFilters((prevFilters) => [...prevFilters, ...newFilters]);
    handleClose();
  };

  const handleClearFilters = () => {
    setFilters([]);

    setVInvoiceNumber([]);

    setInstamartDNNumber([]);

    setOrganizationName([]);

    setPoNumber([]);

    handleClose();
  };

  function extractUniqueOptions<T extends string>(data: T[], field: string): { [key: string]: T }[] {
    // const values = [EXCLUDE, ...new Set(data.filter((val) => typeof val === "string" && val.trim() !== ""))];
    const values = [...new Set(data.filter((val) => typeof val === "string" && val.trim() !== ""))];
    return values.map((val) => ({ [field]: val } as { [key: string]: T }));
  }

  const allInvoiceOptions = useMemo(() => {
    const allInvoiceOptionsValues = data
      .map((item) => item.invoiceNumber)
      .filter((val) => typeof val === "string" && val.trim() !== "");
    return extractUniqueOptions<string>(allInvoiceOptionsValues, "invoiceNumber");
  }, [data]);

  const allInstamartDNOptions = useMemo(() => {
    const allInstamartDNOptionsValues = data
      .map((item) => item.instamartDNNumber)
      .filter((val) => typeof val === "string" && val.trim() !== "");
    return extractUniqueOptions<string>(allInstamartDNOptionsValues, "instamartDNNumber");
  }, [data]);

  const allPoNumberOptions = useMemo(() => {
    const allPoNumberOptionsValues = data
      .map((item) => item.poNumber)
      .filter((val) => typeof val === "string" && val.trim() !== "");
    return extractUniqueOptions<string>(allPoNumberOptionsValues, "poNumber");
  }, [data]);
  const allOrganizationNameOptions = useMemo(() => {
    const allOrganizationNameOptionsValues = data
      .map((item) => item.organizationName)
      .filter((val) => typeof val === "string" && val.trim() !== "");
    return extractUniqueOptions<string>(allOrganizationNameOptionsValues, "organizationName");
  }, [data]);
  const instamartDNNumberOptions = useMemo(() => {
    if (!debouncedInstamartDNInput) return allInstamartDNOptions.slice(0, 100);
    return allInstamartDNOptions.filter((opt) =>
      opt.instamartDNNumber.toLowerCase().includes(debouncedInstamartDNInput.toLowerCase())
    );
  }, [allInstamartDNOptions, debouncedInstamartDNInput]);

  const invoiceNumberOptions = useMemo(() => {
    if (!debouncedInvoiceInput) return allInvoiceOptions.slice(0, 100);
    return allInvoiceOptions.filter((opt) =>
      opt.invoiceNumber.toLowerCase().includes(debouncedInvoiceInput.toLowerCase())
    );
  }, [allInvoiceOptions, debouncedInvoiceInput]);

  const poNumberOptions = useMemo(() => {
    if (!debouncedPoInput) return allPoNumberOptions.slice(0, 100);
    return allPoNumberOptions.filter((opt) => opt.poNumber.toLowerCase().includes(debouncedPoInput.toLowerCase()));
  }, [allPoNumberOptions, debouncedPoInput]);

  const organizationNameOptions = useMemo(() => {
    if (!debouncedOrganizationNameInput) return allOrganizationNameOptions.slice(0, 100);
    return allOrganizationNameOptions.filter((opt) =>
      opt.organizationName.toLowerCase().includes(debouncedOrganizationNameInput.toLowerCase())
    );
  }, [allOrganizationNameOptions, debouncedOrganizationNameInput]);

  return (
    <div>
      <Dialog open={openFilterOfDisputedEntries} maxWidth={DIALOG_MAX_WIDTHS.SM} fullWidth>
        <DialogTitle className="dialog-title">
          <div className="heading-m">Filter</div>
          <div className="vertical_center_align">
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Organization Name</Typography>
            <Box sx={{ width: "350px" }}>
              <TagsInputUniqueKeys
                tags={organizationName}
                setTags={setOrganizationName}
                className="organizationNameAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select Organization Name"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={organizationNameOptions}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={option.organizationName === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}
                  >
                    {option.organizationName}
                  </li>
                )}
                accessor={"organizationName"}
                uniqueKeyAccessor={"organizationName"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={organizationNameInput}
                onInputChange={(_, val) => setOrganizationNameInput?.(val)}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Vendor Invoice Number</Typography>
            <Box sx={{ width: "350px" }}>
              <TagsInputUniqueKeys
                tags={invoiceNumber}
                setTags={setVInvoiceNumber}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select Invoice Number"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={invoiceNumberOptions}
                renderOption={(props, option) => (
                  <li {...props} style={option.invoiceNumber === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}>
                    {option.invoiceNumber}
                  </li>
                )}
                accessor={"invoiceNumber"}
                uniqueKeyAccessor={"invoiceNumber"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={invoiceInput}
                onInputChange={(_, val) => setInvoiceInput?.(val)}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Instamart DN Number</Typography>
            <Box sx={{ width: "350px" }}>
              <TagsInputUniqueKeys
                tags={instamartDNNumber}
                setTags={setInstamartDNNumber}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select Instamart DN Number"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={instamartDNNumberOptions}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={option.instamartDNNumber === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}
                  >
                    {option.instamartDNNumber}
                  </li>
                )}
                accessor={"instamartDNNumber"}
                uniqueKeyAccessor={"instamartDNNumber"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={instamartDNInput}
                onInputChange={(_, val) => setInstamartDNInput?.(val)}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">PO Number</Typography>
            <Box sx={{ width: "350px" }}>
              <TagsInputUniqueKeys
                tags={poNumber}
                setTags={setPoNumber}
                className="poNumberAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select PO Number"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={poNumberOptions}
                renderOption={(props, option) => (
                  <li {...props} style={option.poNumber === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}>
                    {option.poNumber}
                  </li>
                )}
                accessor={"poNumber"}
                uniqueKeyAccessor={"poNumber"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={poInput}
                onInputChange={(_, val) => setPoInput?.(val)}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions className="dialog-actions">
          <CustomButton size="medium" colorToken="violet" variant="text" onClick={handleClearFilters}>
            Clear Filter
          </CustomButton>
          <CustomButton
            size="medium"
            colorToken="violet"
            variant="contained"
            //    disabled={isEditing}
            //    startIcon={<LoadingIcon loading={isEditing} />}
            onClick={() => {
              handleApplyFilter();
            }}
          >
            Apply
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default NonReconVpFilterOfDisputedEntries;
